import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signUp as amplifySignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function signUp(email, password) {
    const { nextStep } = await amplifySignUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
    return nextStep;
  }

  async function confirmSignUp(email, code) {
    await amplifyConfirmSignUp({ username: email, confirmationCode: code });
  }

  async function signIn(email, password) {
    const { nextStep } = await amplifySignIn({ username: email, password });
    if (nextStep.signInStep === 'DONE') {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    }
    return nextStep;
  }

  async function signOut() {
    await amplifySignOut({ global: true });
    setUser(null);
  }

  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('No token');
      return token;
    } catch {
      setUser(null);
      throw new Error('Session expired');
    }
  }, []);

  async function resetPassword(email) {
    await amplifyResetPassword({ username: email });
  }

  async function confirmResetPassword(email, code, newPassword) {
    await amplifyConfirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    getToken,
    resetPassword,
    confirmResetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
